/**
 * @file Project detail field renderer.
 */

import { appData } from '../../app/state/state.js';
import {
    formatCurrency,
} from "../../shared/utils/displayUtils.js";
import {
    renderAuditTrail,
    renderLongTextDetailSection,
} from '../../shared/details/detailSections.js';
import {
    createDateControl,
} from '../../shared/ui/controls/dateControl.js';
import {
    createPrioritySelectPreset,
    createStatusSelectPreset,
    createTypeSelectPreset,
    createUserSelectPreset,
} from '../../shared/ui/controls/controlPresets.js';
import {
    DETAIL_TITLE_INPUT_CLASSES,
    NATIVE_INPUT_CLASSES,
    getNativeFieldWrapperHtml,
} from '../../shared/forms/nativeFieldStyles.js';

function attrValue(value = '') {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function inputHtml(type, name, value = '', options = {}) {
    const placeholderAttr = options.placeholder ? ` placeholder="${attrValue(options.placeholder)}"` : '';
    const extraAttrs = options.extraAttrs ? ` ${options.extraAttrs}` : '';
    const className = [NATIVE_INPUT_CLASSES, options.className || '']
        .filter(Boolean)
        .join(' ');
    return `<input type="${type}" name="${name}" value="${attrValue(value)}" class="${className}"${placeholderAttr}${extraAttrs}>`;
}

function scalarControlRootHtml(fieldName) {
    return `<div class="min-w-0" data-detail-scalar-control="${fieldName}"></div>`;
}

/**
 * Generates HTML for editable Project detail fields.
 * @param {object} item Project item data.
 * @returns {string} Project detail fields HTML.
 */
export function getProjectDetailFields(item) {
    const val = (field, fallback = '') => item?.[field] ?? fallback;

    return `
        <div class="bg-white pt-6 px-6 pb-3 rounded-lg shadow-md mb-6">
            <div class="mb-2 pb-2">
                <textarea name="name" class="${DETAIL_TITLE_INPUT_CLASSES} resize-none overflow-hidden" placeholder="Project Name" aria-label="Project Name" autocomplete="off" rows="1" data-auto-grow="true" required>${attrValue(val('name'))}</textarea>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6 text-sm mt-4">
                ${getNativeFieldWrapperHtml('Project Type(s)', scalarControlRootHtml('types'), { className: 'md:col-span-2' })}
                ${getNativeFieldWrapperHtml('Project Manager(s)', scalarControlRootHtml('project_manager'), { className: 'md:col-span-2' })}

                ${getNativeFieldWrapperHtml('Urgency', scalarControlRootHtml('priority'))}
                ${getNativeFieldWrapperHtml('Status', scalarControlRootHtml('status'))}
                ${getNativeFieldWrapperHtml('Delivery Date', scalarControlRootHtml('delivery_date'))}
                ${getNativeFieldWrapperHtml('Budget', inputHtml('text', 'budget', formatCurrency(val('budget')), { className: 'currency-input', placeholder: '$', extraAttrs: 'inputmode="decimal" data-native-currency="true"' }))}

                ${renderLongTextDetailSection({
        fieldName: 'comments',
        label: 'Details',
        value: val('comments'),
        wrapperClass: 'md:col-span-4',
    })}
            </div>
            ${renderAuditTrail(item)}
        </div>

    `;
}

/**
 * Mounts registered type controls for Project details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Project item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupProjectDetailTypeControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    const root = form?.querySelector('[data-detail-scalar-control="types"]');
    if (!root) {return controls;}

    controls.types = createTypeSelectPreset({
        root,
        appData,
        itemType: 'project',
        value: Array.isArray(item?.types) ? item.types : [],
        searchable: true,
    });

    return controls;
}

/**
 * Mounts registered manager controls for Project details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Project item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupProjectDetailPeopleControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const managerRoot = form.querySelector(
        '[data-detail-scalar-control="project_manager"]',
    );
    if (managerRoot) {
        controls.project_manager = createUserSelectPreset({
            root: managerRoot,
            appData,
            name: 'project_manager',
            collectionName: 'projects',
            value: Array.isArray(item?.project_manager) ? item.project_manager : [],
            multiple: true,
            searchable: true,
        });
    }

    return controls;
}

/**
 * Mounts registered scalar controls for Project details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Project item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupProjectDetailScalarControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const getRoot = (fieldName) => form.querySelector(
        `[data-detail-scalar-control="${fieldName}"]`,
    );
    const value = (fieldName) => item?.[fieldName] ?? '';

    const priorityRoot = getRoot('priority');
    if (priorityRoot) {
        controls.priority = createPrioritySelectPreset({
            root: priorityRoot,
            appData,
            value: value('priority'),
            clearable: false,
        });
    }

    const statusRoot = getRoot('status');
    if (statusRoot) {
        controls.status = createStatusSelectPreset({
            root: statusRoot,
            appData,
            itemType: 'project',
            value: value('status'),
            clearable: false,
        });
    }

    const deliveryDateRoot = getRoot('delivery_date');
    if (deliveryDateRoot) {
        controls.delivery_date = createDateControl({
            root: deliveryDateRoot,
            name: 'delivery_date',
            type: 'date',
            value: value('delivery_date'),
        });
    }

    return controls;
}
